import React, { Fragment, useMemo } from 'react';

import {
  GroupBandRow,
  labelCell,
  MONTH_ROW,
  MONTH_TABLE,
  MonthHeaderRow,
  numberTone,
  valueCell,
  type MonthColumn,
} from './month-table';

import type { ActiveStatusCell, MarketingActiveStatusResponse } from '@utazzo/shared';

import { ReportSection } from '@/features/reports/components/ReportSection';

const count = (value: number): string => value.toLocaleString('en-IN');

/**
 * The five sheet stages, in funnel order. `total` is rendered separately.
 *
 * `sense` is what the stage means for the business, which decides the figure's
 * colour - the two ends of the funnel are the ones worth finding at a glance.
 */
const STAGES: Array<{ key: keyof ActiveStatusCell; label: string; sense?: 'good' | 'bad' }> = [
  { key: 'lost', label: 'Lost', sense: 'bad' },
  { key: 'proposal', label: 'Contacted (Proposal)' },
  { key: 'warm', label: 'Qualified (Warm)' },
  { key: 'hot', label: 'Booked (Hot)' },
  { key: 'won', label: 'Converted (Won)', sense: 'good' },
];

/** One channel's block: its per-month cells plus the total across them. */
interface ChannelBlock {
  key: string;
  name: string;
  totals: ActiveStatusCell;
  /** month key -> cell. Months a channel has no leads in are simply absent. */
  byMonth: Map<string, ActiveStatusCell>;
}

const emptyCell = (): ActiveStatusCell => ({
  lost: 0,
  proposal: 0,
  warm: 0,
  hot: 0,
  won: 0,
  total: 0,
  other: 0,
});

const addInto = (into: ActiveStatusCell, from: ActiveStatusCell): void => {
  into.lost += from.lost;
  into.proposal += from.proposal;
  into.warm += from.warm;
  into.hot += from.hot;
  into.won += from.won;
  into.total += from.total;
  into.other += from.other;
};

/**
 * "Month Wise Active Status" - the funnel standing of each channel's queries,
 * month by month.
 *
 * Laid out like every other month table here: months across the top (newest
 * first), the five funnel stages down the side, one band per channel. Each lead
 * falls in exactly one stage (outcome buckets take precedence over the active
 * sub-status), so the stages sum to the Total row safely. Leads that reached
 * none of the five - still uncontacted or not qualified - are excluded from the
 * totals, matching the source sheet, and reported in a footnote so they aren't
 * silently lost.
 */
export const ActiveStatusTable = ({
  data,
  loading,
}: {
  data: MarketingActiveStatusResponse | undefined;
  loading: boolean;
}): React.JSX.Element => {
  const excluded = data?.grandTotal.other ?? 0;

  /**
   * Month columns are the union across channels: the API omits months a channel
   * has no leads in (an empty row adds no information), but as COLUMNS they
   * have to line up, so every month any channel saw becomes a column and the
   * gaps render as zero.
   */
  const months = useMemo<MonthColumn[]>(() => {
    const seen = new Map<string, string>();
    for (const channel of data?.channels ?? []) {
      for (const month of channel.months) seen.set(month.month, month.label);
    }
    // Newest first, matching the rest of the report.
    return [...seen.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([month, label]) => ({ month, label }));
  }, [data]);

  /** Channel blocks, with a synthetic Grand Total block appended. */
  const blocks = useMemo<ChannelBlock[]>(() => {
    const channels = (data?.channels ?? []).map((channel) => ({
      key: channel.key,
      name: channel.name,
      totals: channel.totals,
      byMonth: new Map(channel.months.map((month) => [month.month, month])),
    }));
    if (channels.length === 0) return channels;

    // The API's grandTotal is a single cell across the whole range, so the
    // per-month grand totals are summed here rather than round-tripped.
    const grandByMonth = new Map<string, ActiveStatusCell>();
    for (const channel of channels) {
      for (const [month, cell] of channel.byMonth) {
        const into = grandByMonth.get(month) ?? emptyCell();
        addInto(into, cell);
        grandByMonth.set(month, into);
      }
    }
    return [
      ...channels,
      {
        key: '__grand_total__',
        name: 'Grand Total',
        totals: data!.grandTotal,
        byMonth: grandByMonth,
      },
    ];
  }, [data]);

  return (
    <ReportSection title="Month Wise Active Status">
      {loading || !data ? (
        <div className="space-y-2 py-2">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="bg-muted h-8 animate-pulse rounded" />
          ))}
        </div>
      ) : data.channels.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          No leads in the selected range.
        </p>
      ) : (
        <>
          <table className={MONTH_TABLE}>
            <thead>
              <MonthHeaderRow label="Channel / Stage" months={months} trailing="Total" />
            </thead>
            <tbody>
              {blocks.map((block) => {
                const cellFor = (month: string): ActiveStatusCell =>
                  block.byMonth.get(month) ?? emptyCell();
                return (
                  <Fragment key={block.key}>
                    <GroupBandRow span={months.length + 1} tone="entity">
                      {block.name}
                    </GroupBandRow>
                    {STAGES.map((stage) => (
                      <tr key={stage.key} className={MONTH_ROW}>
                        <th scope="row" className={labelCell()}>
                          {stage.label}
                        </th>
                        {months.map((month) => (
                          <td
                            key={month.month}
                            className={valueCell({
                              tone: numberTone(cellFor(month.month)[stage.key], stage.sense),
                            })}
                          >
                            {count(cellFor(month.month)[stage.key])}
                          </td>
                        ))}
                        <td
                          className={valueCell({
                            headline: true,
                            tone: numberTone(block.totals[stage.key], stage.sense),
                          })}
                        >
                          {count(block.totals[stage.key])}
                        </td>
                      </tr>
                    ))}
                    <tr className={MONTH_ROW}>
                      <th scope="row" className={labelCell({ headline: true })}>
                        Total
                      </th>
                      {months.map((month) => (
                        <td key={month.month} className={valueCell({ headline: true })}>
                          {count(cellFor(month.month).total)}
                        </td>
                      ))}
                      <td className={valueCell({ headline: true })}>{count(block.totals.total)}</td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          {excluded > 0 && (
            <p className="text-muted-foreground mt-3 text-xs">
              {count(excluded)} lead(s) in the range reached none of these five stages (still
              uncontacted or not qualified) and are excluded from the totals above.
            </p>
          )}
        </>
      )}
    </ReportSection>
  );
};
